import type { FC, ReactNode } from 'react'
import type { Bi } from '@/sanskrit/lib/sanskrit/types'
import type { SamasaExample, SamasaType } from '@/sanskrit/lib/sanskrit/samasa'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowRight, Check, Plus, RotateCcw, X } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/sanskrit/components/vani/bits'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { recordAnswer } from '@/sanskrit/lib/progress/engine'
import { seededShuffle } from '@/sanskrit/lib/random'
import { SAMASA_EXAMPLES, SAMASA_TYPES } from '@/sanskrit/lib/sanskrit/samasa'
import AudioButton from '@/sanskrit/components/vani/audio-button'

type Head = 'purva' | 'uttara' | 'both' | 'anya'

const TYPE_ORDER: SamasaType[] = ['avyayibhava', 'tatpurusa', 'karmadharaya', 'dvigu', 'dvandva', 'bahuvrihi']

/** Which member carries the main meaning, as the tradition states it. */
const HEAD: Record<SamasaType, { head: Head; sa: string; text: Bi }> = {
    avyayibhava: { head: 'purva', sa: 'पूर्वपदार्थप्रधानः', text: { hi: 'पहले पद का अर्थ प्रधान', en: 'The first member carries the main meaning' } },
    tatpurusa: { head: 'uttara', sa: 'उत्तरपदार्थप्रधानः', text: { hi: 'दूसरे पद का अर्थ प्रधान', en: 'The second member carries the main meaning' } },
    karmadharaya: { head: 'uttara', sa: 'उत्तरपदार्थप्रधानः', text: { hi: 'दूसरे पद का अर्थ प्रधान', en: 'The second member carries the main meaning' } },
    dvigu: { head: 'uttara', sa: 'उत्तरपदार्थप्रधानः', text: { hi: 'दूसरे पद का अर्थ प्रधान', en: 'The second member carries the main meaning' } },
    dvandva: { head: 'both', sa: 'उभयपदार्थप्रधानः', text: { hi: 'दोनों पद बराबर प्रधान', en: 'Both members are equally important' } },
    bahuvrihi: { head: 'anya', sa: 'अन्यपदार्थप्रधानः', text: { hi: 'कोई तीसरा, जो समास में लिखा ही नहीं, प्रधान', en: 'Something outside the compound is meant' } }
}

const TYPE_TONE: Record<SamasaType, string> = {
    avyayibhava: 'bg-tulsi-soft',
    tatpurusa: 'bg-saffron-soft',
    karmadharaya: 'bg-terracotta-soft',
    dvigu: 'bg-ink-soft',
    dvandva: 'bg-saffron-soft',
    bahuvrihi: 'bg-terracotta-soft'
}

function PartsDiagram({ ex }: { ex: SamasaExample }) {
    const t = useT()
    const head = HEAD[ex.type].head

    return (
        <div className='flex flex-wrap items-center gap-2'>
            {ex.parts.map((part, i) => {
                const isHead = head === 'both' || (head === 'purva' && i === 0) || (head === 'uttara' && i === ex.parts.length - 1)

                return (
                    <span key={`${part}-${i}`} className='flex items-center gap-2'>
                        {i > 0 && <Plus className='size-4 text-muted-foreground' aria-hidden='true' />}
                        <span className={cn('rounded-xl border-2 px-3 py-1', isHead ? 'border-saffron-strong bg-saffron text-ink' : 'border-border bg-background')}>
                            <span lang='sa' className='deva text-xl font-semibold'>{part}</span>
                        </span>
                    </span>
                )
            })}
            {head === 'anya' && (
                <span className='flex items-center gap-2'>
                    <ArrowRight className='size-4 text-muted-foreground' aria-hidden='true' />
                    <span className='rounded-xl border-2 border-dashed border-terracotta bg-terracotta-soft px-3 py-1 text-sm font-semibold'>{t(ex.meaning)}</span>
                </span>
            )}
        </div>
    )
}

function ExampleCard({ ex }: { ex: SamasaExample }) {
    const t = useT()
    const [open, setOpen] = useState(false)

    return (
        <article className='flex flex-col rounded-[1.5rem] border border-border bg-card p-5 shadow-sm'>
            <div className='flex items-start justify-between gap-2'>
                <div>
                    <p lang='sa' className='deva text-3xl font-semibold text-ink'>{ex.compound}</p>
                    <p className='text-sm text-muted-foreground'>{t(ex.meaning)}</p>
                </div>
                <AudioButton text={ex.compound} size='sm' />
            </div>
            <div className='mt-3'>
                <PartsDiagram ex={ex} />
            </div>
            <AnimatePresence initial={false} mode='wait'>
                {open
                    ? (
                        <motion.div key='open' initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className='mt-4 rounded-2xl bg-muted/60 p-3'>
                            <p className='text-xs font-semibold text-muted-foreground'>{t({ hi: 'विग्रह (खोलकर लिखा रूप)', en: 'Vigraha (the compound spelled out)' })}</p>
                            <p lang='sa' className='deva mt-1 text-xl font-semibold'>{ex.vigraha}</p>
                            {ex.subtype && <p className='mt-1 text-xs text-muted-foreground'>{t(ex.subtype)}</p>}
                        </motion.div>
                    )
                    : null}
            </AnimatePresence>
            <button type='button' onClick={() => setOpen(o => !o)} className='mt-4 self-start rounded-full border border-border px-3 py-1.5 text-sm font-semibold transition-colors hover:border-saffron'>
                {open ? t({ hi: 'विग्रह छिपाएँ', en: 'Hide the vigraha' }) : t({ hi: 'विग्रह देखें', en: 'Show the vigraha' })}
            </button>
        </article>
    )
}

function TypeQuiz() {
    const t = useT()
    const { updateProgress } = useAppState()
    const order = useMemo(() => seededShuffle(SAMASA_EXAMPLES, 'samasa-quiz'), [])
    const [index, setIndex] = useState(0)
    const [picked, setPicked] = useState<SamasaType | null>(null)
    const [score, setScore] = useState({ right: 0, total: 0 })
    const ex = order[index % order.length]
    const done = picked !== null

    const choose = (type: SamasaType) => {
        if (done) return
        const ok = type === ex.type
        setPicked(type)
        setScore(s => ({ right: s.right + (ok ? 1 : 0), total: s.total + 1 }))
        updateProgress(p => recordAnswer(p, { item: `samasa:${ex.id}`, skill: 'sandhi', ok }))
    }

    const next = () => {
        setPicked(null)
        setIndex(i => i + 1)
    }

    return (
        <section id='quiz' className='rounded-[2rem] border border-border bg-card p-5 shadow-sm sm:p-7'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
                <h2 className='font-heading text-2xl'>{t({ hi: 'कौन सा समास?', en: 'Which compound is it?' })}</h2>
                <p className='text-sm text-muted-foreground'>{t({ hi: `सही: ${score.right} / ${score.total}`, en: `Correct: ${score.right} of ${score.total}` })}</p>
            </div>
            <div className='mt-4 rounded-2xl bg-saffron-soft/70 p-4'>
                <p lang='sa' className='deva text-3xl font-semibold text-ink'>{ex.compound}</p>
                <p className='mt-1 text-sm'>
                    {t({ hi: 'विग्रह:', en: 'Vigraha:' })} <span lang='sa' className='deva font-semibold'>{ex.vigraha}</span>
                </p>
            </div>
            <div className='mt-4 grid gap-2 sm:grid-cols-3'>
                {TYPE_ORDER.map(type => {
                    const right = done && type === ex.type
                    const wrong = done && type === picked && type !== ex.type

                    return (
                        <button
                            key={type}
                            type='button'
                            onClick={() => choose(type)}
                            disabled={done}
                            className={cn(
                                'flex min-h-14 items-center justify-between gap-2 rounded-2xl border-2 px-4 py-2 text-left transition-colors',
                                right ? 'border-tulsi bg-tulsi-soft' : wrong ? 'border-destructive bg-destructive/10' : 'border-border bg-background hover:border-saffron',
                                done && !right && !wrong && 'opacity-60'
                            )}
                        >
                            <span>
                                <span lang='sa' className='deva block text-lg font-semibold'>{SAMASA_TYPES[type].sa}</span>
                                <span className='block text-xs text-muted-foreground'>{t(SAMASA_TYPES[type].name)}</span>
                            </span>
                            {right && <Check className='size-5 text-tulsi' />}
                            {wrong && <X className='size-5 text-destructive' />}
                        </button>
                    )
                })}
            </div>
            {done && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className='mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-muted/60 p-4'>
                    <p className='max-w-2xl text-sm'>
                        <span className='font-semibold'>{picked === ex.type ? t({ hi: 'सही!', en: 'Right!' }) : t({ hi: `यह ${SAMASA_TYPES[ex.type].name.hi} है।`, en: `This is ${SAMASA_TYPES[ex.type].name.en}.` })}</span>{' '}
                        {t(SAMASA_TYPES[ex.type].test)}
                    </p>
                    <Button onClick={next}>
                        {t({ hi: 'अगला', en: 'Next' })}
                        <ArrowRight className='size-4' />
                    </Button>
                </motion.div>
            )}
        </section>
    )
}

const SamasaPage: FC = (): ReactNode => {
    const t = useT()
    const [type, setType] = useState<SamasaType | 'all'>('all')
    const examples = type === 'all' ? SAMASA_EXAMPLES : SAMASA_EXAMPLES.filter(e => e.type === type)

    return (
        <Layout>
            <Seo title='Samāsa' description='The six kinds of Sanskrit compound with their analysis (vigraha), which member carries the meaning, and a quick identification game.' />

            <SanskritGate what='The samāsa lab'>
            <PageHeader
                sa='समासः'
                title={{ hi: 'समास: छोटा करके कहना', en: 'Samāsa: saying more with fewer words' }}
                subtitle={{
                    hi: 'जब दो या अधिक शब्द मिलकर एक शब्द बनते हैं और बीच की विभक्तियाँ हट जाती हैं, तो वह समास है। उसे फिर से खोलकर लिखना विग्रह कहलाता है।',
                    en: 'When two or more words merge into one and the case endings in between disappear, that is a samāsa. Spelling it back out is called its vigraha.'
                }}
            />

            <div className='mx-auto max-w-6xl space-y-8 px-4 pb-16 sm:px-6'>
                <section className='rounded-[2rem] bg-ink p-6 text-primary-foreground sm:p-8'>
                    <p className='text-sm text-primary-foreground/70'>{t({ hi: 'एक उदाहरण', en: 'One example' })}</p>
                    <div className='mt-2 flex flex-wrap items-center gap-3 text-2xl sm:text-3xl'>
                        <span lang='sa' className='deva font-semibold'>राज्ञः पुरुषः</span>
                        <ArrowRight className='size-6 text-saffron' aria-hidden='true' />
                        <span lang='sa' className='deva font-semibold text-saffron'>राजपुरुषः</span>
                    </div>
                    <p className='mt-3 max-w-3xl text-primary-foreground/80'>
                        {t({
                            hi: '"राजा का सेवक" दो शब्द हैं; "राजपुरुषः" एक। षष्ठी का "राज्ञः" हटकर मूल शब्द "राज" रह गया।',
                            en: '"The king’s officer" is two words; राजपुरुषः is one. The genitive राज्ञः lost its ending and only the stem राज remains.'
                        })}
                    </p>
                </section>

                <section>
                    <h2 className='mb-3 font-heading text-2xl'>{t({ hi: 'छह प्रकार', en: 'The six kinds' })}</h2>
                    <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {TYPE_ORDER.map(id => {
                            const info = SAMASA_TYPES[id]

                            return (
                                <button
                                    key={id}
                                    type='button'
                                    onClick={() => setType(type === id ? 'all' : id)}
                                    aria-pressed={type === id}
                                    className={cn('rounded-[1.5rem] border-2 p-4 text-left transition-all', TYPE_TONE[id], type === id ? 'border-ink shadow-md' : 'border-transparent hover:border-saffron')}
                                >
                                    <p lang='sa' className='deva text-xl font-semibold text-ink'>{info.sa}</p>
                                    <p className='text-sm font-semibold'>{t(info.name)}</p>
                                    <p className='mt-2 text-sm text-muted-foreground'>{t(info.test)}</p>
                                    <p className='mt-2 text-xs'>
                                        <span lang='sa' className='deva font-semibold'>{HEAD[id].sa}</span>
                                        <span className='text-muted-foreground'> · {t(HEAD[id].text)}</span>
                                    </p>
                                </button>
                            )
                        })}
                    </div>
                </section>

                <section>
                    <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
                        <h2 className='font-heading text-2xl'>
                            {type === 'all' ? t({ hi: 'सभी उदाहरण', en: 'All examples' }) : t(SAMASA_TYPES[type].name)}
                        </h2>
                        {type !== 'all' && (
                            <Button variant='outline' size='sm' onClick={() => setType('all')}>
                                <RotateCcw className='size-4' />
                                {t({ hi: 'सभी दिखाएँ', en: 'Show all' })}
                            </Button>
                        )}
                    </div>
                    <p className='mb-4 text-sm text-muted-foreground'>
                        {t({ hi: 'केसरिया खाना बताता है कि अर्थ में कौन सा पद प्रधान है।', en: 'The saffron box marks the member that carries the main meaning.' })}
                    </p>
                    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {examples.map(ex => <ExampleCard key={ex.id} ex={ex} />)}
                    </div>
                </section>

                <TypeQuiz />

                <Link href='/sanskrit/practice/samasa-pehchan' className='flex items-center justify-between rounded-3xl bg-ink p-5 text-primary-foreground'>
                    <span>
                        <span lang='sa' className='deva block text-xl font-semibold'>समास पहचान</span>
                        <span className='text-sm text-primary-foreground/75'>{t({ hi: 'पूरा अभ्यास खेल, अंकों के साथ', en: 'The full practice game, with scoring' })}</span>
                    </span>
                    <ArrowRight className='size-5' />
                </Link>
            </div>
            </SanskritGate>
        </Layout>
    )
}

export default SamasaPage
